import React, { useEffect, useState } from 'react';
import { terms, viewTypes } from '../constants';
import { HelixIcon } from '@lwt-helix/helix-icon';
import {
    arrow_chart_line_down,
    arrow_chart_line,
    chevron_small_right,
    dash_indeterminate
} from '@lwt-helix/helix-icon/outlined';
import { agentProductionTerms, moduleNames } from '../../constants';
import {
    SegmentedControl,
    SegmentedControlItem,
    SegmentedControlItemBar
} from '@helix/segmented-control';
import { getAgentProductivityLtm } from '../../service/broker-metrics';
import { useCommonStore, useSearchStore } from '../../store/store';
import clsx from 'clsx';
import {
    formatPercentageWithMultiplier,
    getCompactDollars
} from '../../agent-production/helpers/dataFormatters';
import Loader from '@lwt-helix/loader';
import { useHistory } from 'react-router-dom';
import { getProductURL } from '../../common/helpers/nav';
import { useAuthStore } from '../../store/auth/store';

const AgentProductivity = () => {
    const history = useHistory();
    const userProducts = useCommonStore((state) => state.privileges?.userProducts);
    const isLegacyUser = useAuthStore((state) => state.isLegacyUser);

    const { selectedMls, selectedOffice, viewTypeId } = useSearchStore((state) => ({
        selectedMls: state[state.selectedModule]?.selectedMls,
        selectedOffice: state?.[state.selectedModule]?.selectedOffice,
        viewTypeId: state?.[state.selectedModule]?.viewTypeId
    }));
    const [state, setState] = useState({
        activeTab: 'top-producers',
        ['top-producers']: [],
        ['at-risk']: [],
        isLoading: true,
        error: null
    });

    const agentData = state[state.activeTab] ?? [];
    const sortedAgentData =
        agentData
            ?.slice()
            .sort((a, b) => {
                if (state.activeTab === 'top-producers') {
                    return b.volume - a.volume;
                } else {
                    return a.percentChange - b.percentChange;
                }
            })
            .map((agent, index) => ({
                id: agent.agentId,
                initials:
                    agent?.agentName
                        ?.split(' ')
                        .map((name) => name[0])
                        .join('')
                        .slice(0, 2) ?? '',
                name: agent?.agentName ?? 'Agent',
                rank: index + 1,
                sales: getCompactDollars(agent.volume),
                percentChange: agent.percentChange
            })) || [];

    const hasAgentData = agentData.length > 0;

    useEffect(() => {
        const fetchAgentProductivity = async () => {
            const selectedMlsId = selectedMls?.mlsId.toString();
            setState((prevState) => ({
                ...prevState,
                activeTab: 'top-producers',
                ['at-risk']: [],
                ['top-producers']: []
            }));
            if (isLegacyUser) {
                setState((prevState) => ({ ...prevState, isLoading: false }));
                return;
            }
            if (!selectedMlsId) return;

            const officeIds = viewTypeId === viewTypes.byOffice.id && selectedOffice?.mlsOfficeId?.officeId;
            const brokerIds = viewTypeId === viewTypes.byBrokerage.id && selectedOffice?.mlsBrokerId?.brokerId;

            if (!officeIds && !brokerIds) return;

            setState((prevState) => ({ ...prevState, isLoading: true }));
            try {
                const response = await getAgentProductivityLtm({
                    mlsId: selectedMlsId,
                    officeIds,
                    brokerIds
                });
                const atRiskAgents = response.ltmPtmAnalysis?.atRiskAgents?.agentList ?? [];
                const topProducingAgents =
                    response.ltmPtmAnalysis?.topProducingAgents?.agentList ?? [];
                setState((prevState) => ({
                    ...prevState,
                    ['at-risk']: atRiskAgents,
                    ['top-producers']: topProducingAgents
                }));
            } catch (error) {
                setState((prevState) => ({ ...prevState, error }));
                console.error(error);
            } finally {
                setState((prevState) => ({ ...prevState, isLoading: false }));
            }
        };
        fetchAgentProductivity();
    }, [
        selectedMls,
        selectedOffice?.mlsOfficeId?.officeId,
        viewTypeId,
        selectedOffice?.mlsBrokerId?.brokerId
    ]);

    const handleNavigateToProficiencyMetrics = () => {
        history.push(getProductURL({product: moduleNames.proficiencyMetrics, userProducts, isLegacyUser}));
    };

    const hasProficiencyMetricsAccess = !!(userProducts?.includes(moduleNames.proficiencyMetrics) || isLegacyUser);

    if (state.isLoading) {
        return (
            <div className="content dashboard-card p-4 h-100">
                <Loader />
                {/* The skeleton is going to be implemented in further tickets, we are using the loading spinner as the other dashboard components to keep consistency in the UI  */}
                {/* <div className="d-flex justify-content-between align-items-start mb-3">
                    <SkeletonBox boxWidth="150px" />
                    <SkeletonBox boxWidth="100px" />
                </div>
                <div className="d-flex justify-content-center mb-4">
                    <SkeletonBox
                        boxWidth="200px"
                        style={{ height: '2px' }}
                        rounded
                    />
                </div>

                <div className="agent-productivity agent-list">
                    {[1, 2, 3].map((i) => (
                        <div
                            key={i}
                            className="agent-productivity agent-row"
                        >
                            <div className="agent-productivity agent-info">
                                <SkeletonBox boxWidth="1.5rem" />
                                <SkeletonCircle />
                                <SkeletonBox boxWidth="120px" />
                            </div>
                            <div className="agent-productivity agent-stats">
                                <SkeletonBox boxWidth="60px" />
                                <SkeletonBox boxWidth="100px" />
                            </div>
                        </div>
                    ))}
                </div> */}
            </div>
        );
    }

    return (
        <div className="content dashboard-card p-4 h-100">
            <div className="d-flex justify-content-between align-items-start mb-3">
                <h2 className="helix-display-x-small mb-1">{terms.agentProductivity}</h2>
                {hasProficiencyMetricsAccess && (
                    <button
                        className="helix-body--strong dashboard-nav-btn helix-brand-color"
                        onClick={handleNavigateToProficiencyMetrics}
                    >
                        {agentProductionTerms.proficiencyMetrics}
                        <HelixIcon
                            icon={chevron_small_right}
                            className="ml-1"
                        />
                    </button>
                )}
            </div>
            <div className="d-flex justify-content-center mb-4">
                <SegmentedControl
                    defaultActiveKey={'top-producers'}
                    onItemChange={(item) =>
                        setState((prevState) => ({ ...prevState, activeTab: item }))
                    }
                >
                    <SegmentedControlItemBar className="helix-flex-no-shrink">
                        <SegmentedControlItem
                            eventKey="top-producers"
                            aria-checked={state.activeTab === 'top-producers'}
                            style={{ backgroundColor: 'var(--helix-grey)' }}
                        >
                            {terms.topProducers}
                        </SegmentedControlItem>
                        <SegmentedControlItem
                            eventKey="at-risk"
                            aria-checked={state.activeTab === 'at-risk'}
                        >
                            {terms.atRisk}
                        </SegmentedControlItem>
                    </SegmentedControlItemBar>
                </SegmentedControl>
            </div>

            <div className="agent-productivity agent-list">
                {!state.isLoading &&
                    hasAgentData &&
                    sortedAgentData.map((agent) => {
                        const difference = agent.percentChange;
                        const isIncrease = difference > 0;
                        const isDecrease = difference < 0;
                        const isEqual = difference === 0;
                        return (
                            <div
                                key={agent.id}
                                id={agent.id}
                                className="agent-productivity agent-row"
                            >
                                <div className="agent-productivity agent-info">
                                    <div className="agent-productivity agent-rank">
                                        {agent.rank}
                                    </div>
                                    <div className="agent-productivity agent-initials">
                                        {agent.initials}
                                    </div>
                                    <div className="agent-productivity agent-name">
                                        {agent.name}
                                    </div>
                                </div>
                                <div className="agent-productivity agent-stats">
                                    <strong
                                        data-testid="small-widget-difference"
                                        className={clsx(
                                            'd-flex align-items-center font-weight-bold agent-productivity',
                                            {
                                                'increase-agent-sales-volume': isIncrease,
                                                'decrease-agent-sales-volume': isDecrease,
                                                'equal-agent-sales-volume': isEqual
                                            }
                                        )}
                                    >
                                        {isIncrease && (
                                            <HelixIcon
                                                icon={arrow_chart_line}
                                                className="agent-productivity agent-icon"
                                            />
                                        )}
                                        {isDecrease && (
                                            <HelixIcon
                                                icon={arrow_chart_line_down}
                                                className="agent-productivity agent-icon"
                                            />
                                        )}
                                        {isEqual && <HelixIcon icon={dash_indeterminate} />}
                                        {formatPercentageWithMultiplier(
                                            Math.abs(difference),
                                            false,
                                            true
                                        )}
                                    </strong>
                                    <div className="agent-productivity agent-sales">
                                        {agent.sales}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                {!state.isLoading && !hasAgentData && (
                    <div>
                        <h2 className="helix-display-x-small mb-1 text-center">
                            {terms.noAgentActivityTitle}
                        </h2>
                        <p className="text-center">
                            {terms.noAgentActivityMessage1} <br />
                            {terms.noAgentActivityMessage2}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AgentProductivity;
